import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

//========== Import components from MUI Library ============
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";

//All Loading related components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";

//All CSS
import { makeStyles } from "tss-react/mui";
//All Element/ Controls
import CommonLayout from "../../../Common/Layout";
import { ButtonUI, ConfirmPopup } from "../../../Common";
import { findRelations, setPrevState } from "../../../../util/common";
import WorkspaceClass from "../../../../service/workspace";
import { showToast } from "../../../../store/actions";
import HelpContextIcon from "../../../Common/HelpPanel/HelpContextIcon";
import SmallToolbar from "../../../Common/SmallToolbar";

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 4,
    display: "inline-flex",
  },
  subContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    borderTop: 0,
  },
  PanelContainer: {
    padding: "8px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    "& .MuiAlert-message .MuiList-root": {
      listStyle: "square",
    },
  },
  Panel: {
    borderRadius: 10,
    marginBottom: 0,
  },
  SchemaLabel: {
    padding: 0,
    margin: 0,
    paddingBottom: "15px",
    color: "black",
    fontWeight: 600,
    fontSize: "1rem",
    border: "none",
  },
}));

const ConfirmMessage = ({ exportSecretKey }) => {
  const dispatch = useDispatch();

  const handleCopyClick = () => {
    let key = exportSecretKey;
    // copy secret to Clipboard
    if (key !== "")
      navigator.clipboard
        .writeText(key)
        .then(() => {
          dispatch(showToast({ message: "Copied to clipboard", type: "success" }));
        })
        .catch((error) => {});
  };

  return (
    <Box sx={{ maxWidth: "600px" }}>
      <Typography component={"span"}>
        All sensitive connection information contained in the export file will be encrypted with the below key. Make
        sure to copy and store this key in a secure location before proceeding. You will need this key when importing
        the workspace configuration.
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "20px 0 10px 0" }}>
        <Button
          sx={{ mb: 1, backgroundColor: "#FFF5F5", borderRadius: "20px" }}
          onClick={handleCopyClick}
          disabled={!exportSecretKey}
        >
          <KeyOutlinedIcon />
          <Box></Box>
          <Typography sx={{ fontFamily: "monospace", width: "300px" }}>{exportSecretKey}</Typography>
          <ContentCopyOutlinedIcon />
        </Button>
      </Box>
    </Box>
  );
};

const ExportWorkspace = () => {
  const { Workspace_id } = useParams();
  const [loading, setLoading] = useState(false);
  const [exportCompare, setExportCompare] = useState(false);
  const [exportDisableList, setExportDisableList] = useState([]);
  const [options, setOptions] = useState([]);

  const [currentWorkspace, setCurrentWorkspace] = useState({});
  const [checkedValues, setCheckedValues] = useState({});
  const [comparisionList, setComparisionList] = useState({});

  const [configState, setConfigState] = useState({
    uploadedFile: null,
    file: "",
    openConfirm: false,
    loadingForConfirm: false,
    exportSecretKey: "",
  });

  const { classes } = useStyles();
  const dispatch = useDispatch();

  const prepareResultingObject = useCallback(
    (resultingObject, relatedItems) => {
      const autoSelection = relatedItems?.length
        ? findRelations(
            relatedItems,
            comparisionList["datasources"],
            comparisionList["entities"],
            comparisionList["connections"],
            comparisionList["pipelines"]
          )
        : {};

      options?.forEach(({ value }) => {
        const checkedItems = [
          ...new Set([...(resultingObject[value]?.checkedItems || []), ...(autoSelection[value] || [])]),
        ];
        resultingObject[value] = {
          checked: Boolean(checkedItems.length),
          checkedItems,
        };
      });

      return resultingObject;
    },
    [comparisionList, options]
  );

  const handleExportParentCheck = useCallback(
    (e) => {
      let { value, checked } = e.target;
      if (checked) {
        // All First level related items for current selected parent value
        // this will get relations of all the items under this section
        const relatedItems = [...(comparisionList[value]?.flatMap((item) => item?.relations || []) || [])];

        setCheckedValues((prev) =>
          prepareResultingObject(
            {
              ...prev,
              [value]: exportCompare
                ? {
                    checkedItems: comparisionList[value]?.map(({ value }) => value) || [],
                    checked: true,
                  }
                : { checked: true, checkedItems: [] },
            },
            exportCompare ? relatedItems : []
          )
        );
      } else {
        setCheckedValues((prev) => ({
          ...prev,
          [value]: { checked: false, checkedItems: [] },
        }));
      }
    },
    [comparisionList, exportCompare, prepareResultingObject]
  );

  const handleExportChildClick = useCallback(
    (e, parent) => {
      let { value, checked } = e.target;
      // All First level related items for current selected value
      const relatedItems =
        (comparisionList[parent] || [])?.find(({ value: compareValue }) => compareValue === value)?.relations || [];

      if (checked)
        setCheckedValues((prev) =>
          prepareResultingObject(
            {
              ...prev,
              [parent]: {
                checked: true,
                checkedItems: [...prev[parent].checkedItems, value],
              },
            },
            relatedItems
          )
        );
      else {
        let updatedList = checkedValues[parent].checkedItems.filter((val) => val.toLowerCase() !== value.toLowerCase());
        let total = updatedList?.length;
        setCheckedValues((prev) => ({
          ...prev,
          [parent]: { checked: total !== 0, checkedItems: updatedList },
        }));
      }
    },
    [checkedValues, comparisionList, prepareResultingObject]
  );

  const resetCheckboxes = useCallback((options = []) => {
    let resetCheckedValues = {};
    options.forEach(
      ({ value }) =>
        (resetCheckedValues[value] = {
          checked: false,
          checkedItems: [],
        })
    );

    return resetCheckedValues;
  }, []);

  const getCurrentWSConfig = useCallback(async () => {
    setLoading(true);
    const workspace = new WorkspaceClass();
    const response = await workspace.getWorkspaceConfiguration(Workspace_id);
    if (response.success) {
      const tempDisableList = [];
      response.data.comparisonOptionList.forEach((option) => {
        if (response.data.data[option.value].length === 0) {
          tempDisableList.push(option.value);
        }
      });

      let resetCheckedValues = resetCheckboxes(response.data.comparisonOptionList);
      setCheckedValues(resetCheckedValues);

      setOptions(response.data.comparisonOptionList);
      setComparisionList(response.data.data);
      setCurrentWorkspace(response.data.workspace);
      setPrevState(setConfigState, { exportSecretKey: response.data.exportSecretKey });
      setExportDisableList(tempDisableList);
      setExportCompare(true);
    }
    setLoading(false);
  }, [Workspace_id, resetCheckboxes]);

  const onSuccess = useCallback(() => {
    dispatch(
      showToast({
        message: "Workspace configuration exported successfully",
        type: "success",
      })
    );
  }, [dispatch]);

  const handleExport = useCallback(
    async (via = "") => {
      if (via === "popup") setPrevState(setConfigState, { loadingForConfirm: true });
      else setLoading(true);

      const workspace = new WorkspaceClass();
      let submitData = {
        exportSecretKey: configState.exportSecretKey,
      };
      options.forEach((opt) => {
        submitData[opt.value] = checkedValues[opt.value].checkedItems;
      });

      await workspace.exportWorkspace(Workspace_id, `VORTEX_${currentWorkspace.Name}.config`, submitData, onSuccess);

      if (via === "popup") setPrevState(setConfigState, { loadingForConfirm: false, openConfirm: false });
      else setLoading(false);

      getCurrentWSConfig();
    },
    [
      Workspace_id,
      checkedValues,
      configState.exportSecretKey,
      currentWorkspace.Name,
      getCurrentWSConfig,
      onSuccess,
      options,
    ]
  );

  useEffect(() => {
    getCurrentWSConfig();
    return () => {};
  }, [Workspace_id, getCurrentWSConfig]);

  const exportItems = useCallback(
    (option) =>
      comparisionList[option.value]?.map(({ text, value, IsDefault }, i) => {
        return (
          <FormControlLabel
            key={value + i}
            value={value}
            control={
              <Checkbox
                checked={
                  checkedValues[option.value]?.checked &&
                  checkedValues[option.value].checkedItems?.length > 0 &&
                  checkedValues[option.value].checkedItems?.includes(value)
                }
              />
            }
            label={
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography sx={{ m: 0, p: 0 }}>{text}</Typography>
                {IsDefault === 1 && (
                  <Tooltip placement="right" title="Default Pipeline" arrow>
                    <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                  </Tooltip>
                )}
              </Stack>
            }
            onChange={(e) => {
              handleExportChildClick(e, option.value);
            }}
            sx={{
              width: "100%",
              wordBreak: "break-all !important",
              paddingInline: "10px",
              m: 0,
            }}
          />
        );
      }),
    [checkedValues, comparisionList, handleExportChildClick]
  );

  const ExportTableSection = useCallback(() => {
    return (
      <>
        {exportCompare && options.length !== 0 ? (
          options?.map((option, i) => (
            <React.Fragment key={option.value + i}>
              <TableRow key={option.value}>
                <TableCell sx={{ border: "none", padding: 0, display: "flex", alignItems: "center" }}>
                  <FormControlLabel
                    value={option.value}
                    control={
                      <Checkbox
                        checked={checkedValues[option.value].checked && !exportDisableList.includes(option.value)}
                        indeterminate={
                          checkedValues[option.value].checked &&
                          checkedValues[option.value].checkedItems.length !== 0 &&
                          checkedValues[option.value].checkedItems.length !== comparisionList[option.value].length
                        }
                      />
                    }
                    label={option.text}
                    disabled={exportDisableList.includes(option.value)}
                    onChange={handleExportParentCheck}
                    sx={{ margin: 0 }}
                  />
                </TableCell>
              </TableRow>
              {comparisionList[option.value] && (
                <TableRow>
                  <TableCell
                    sx={{
                      border: "none",
                      display: comparisionList[option.value]?.length === 0 ? "none" : "block",
                    }}
                    colSpan={2}
                  >
                    <Box sx={{ maxHeight: "250px", overflow: "auto", backgroundColor: "#f5f5f5" }}>
                      {exportItems(option)}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))
        ) : (
          <></>
        )}
      </>
    );
  }, [checkedValues, comparisionList, exportCompare, exportDisableList, exportItems, handleExportParentCheck, options]);

  const configurarionTable = useCallback(() => {
    return (
      <Grid sx={{ padding: "15px 15px 15px 30px" }} container>
        <Grid sx={{ display: "flex", alignItems: "center" }} size={12}>
          <TableContainer sx={{ width: "100%" }} size="small">
            <Table
              sx={{
                width: "100%",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      minWidth: "180px",
                      width: "290px",
                      maxWidth: "290px",
                      display: "flex",
                      alignItems: "center",
                    }}
                    className={classes.SchemaLabel}
                  >
                    Export
                    {exportCompare &&
                      options?.filter(({ value }) => comparisionList[value]?.length === 0).length === 5 && (
                        <Typography sx={{ ml: "16px" }}>{`(No items to export)`}</Typography>
                      )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{ExportTableSection()}</TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  }, [ExportTableSection, classes.SchemaLabel, comparisionList, exportCompare, options]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    { link: `/my-workspaces/${Workspace_id}`, displayName: currentWorkspace.Name, active: false },
    {
      link: "#",
      displayName: "Export Workspace Configuration",
      active: true,
    },
  ];

  return (
    <CommonLayout title="Settings" loading={loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <AppBar
          sx={{
            position: "relative",
            boxShadow: 0,
            backgroundColor: "#e8edf1",
          }}
        >
          <Toolbar className={classes.Header}>
            <Grid container spacing={5} direction="row" alignItems={"center"}>
              <Grid
                sx={{ display: "flex", alignItems: "center" }}
                size={{
                  sm: 12,
                  xs: 12
                }}>
                <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                  <Typography className={classes.HeaderTitle} component="div">
                    Export Workspace Configuration
                  </Typography>
                </Box>
                <ButtonUI
                  variant="contained"
                  color="success"
                  sx={{ minWidth: "75px", ml: "12px" }}
                  onClick={() => {
                    if (checkedValues["connections"].checked) setPrevState(setConfigState, { openConfirm: true });
                    else handleExport();
                  }}
                  disabled={
                    loading ||
                    !Object.keys(checkedValues).some(
                      (key) => checkedValues[key]?.checked === true && checkedValues[key]?.checkedItems.length !== 0
                    ) ||
                    !exportCompare
                  }
                >
                  Export
                </ButtonUI>
                <Box>
                  <HelpContextIcon screen="Export Workspace" />
                </Box>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          <Box maxWidth="lg" className={classes.subContainer}>
            <Grid container direction="column" justifyContent="flex-end" alignItems="left">
              <Grid className={classes.PanelContainer}>
                <SmallToolbar>Export Workspace Configuration</SmallToolbar>
                <Box sx={{ pl: "5px", pb: "10px" }}>
                  <Typography sx={{ fontSize: "0.9rem", fontStyle: "italic" }}>
                    Export all the related items at once for a consistent update of your workspace/instance.
                  </Typography>
                </Box>
                <Paper elevation={1} className={classes.Panel}>
                  {configurarionTable()}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
      {configState.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: "Export Workspace Configuration",
            popupMessage: <ConfirmMessage exportSecretKey={configState.exportSecretKey} />,
            handleCancel: () => setPrevState(setConfigState, { openConfirm: false }),
            handleClick: () => handleExport("popup"),
            loadingForConfirm: configState.loadingForConfirm,
            confirmBtnName: "Export",
          }}
        />
      )}
    </CommonLayout>
  );
};

export default ExportWorkspace;
